import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getStoredUser } from '@/hooks/useAuth';
import { useSelectedAsset } from '@/hooks/useSelectedAsset';
import { useTheme } from '@/hooks/useTheme';
import { api } from '@/services/api';
import { GenerationForecastDayResponse } from '@/pages/assets/detail/components/forecastTypes';
import DashboardLayout from '@/components/feature/DashboardLayout';
import HomeTelemetryWidget from './components/HomeTelemetryWidget';
import InboxPanel from './components/InboxPanel';
import AlertsPanel from './components/AlertsPanel';
import MarketplacePromo from './components/MarketplacePromo';
import BillingWidget from './components/BillingWidget';
import UeexMarketWidget from './components/UeexMarketWidget';
import OreeHourlyWidget from './components/OreeHourlyWidget';
import SkoInboxWidget from './components/SkoInboxWidget';
import ForecastGenerationSummaryWidget from '@/pages/assets/detail/components/ForecastGenerationSummaryWidget';
import DailyHourlyForecastWidget from '@/pages/assets/detail/components/DailyHourlyForecastWidget';
import PortfolioDashboard from './portfolio/PortfolioDashboard';
import { hasPortfolioHome } from '@/utils/portfolioHome';


function getKyivTodayParts() {
  const formatter = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Kyiv',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(new Date());
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
  };
}

function shiftKyivDate(baseParts: { year: string; month: string; day: string }, offsetDays: number) {
  const base = new Date(`${baseParts.year}-${baseParts.month}-${baseParts.day}T12:00:00Z`);
  const shifted = new Date(base.getTime() + offsetDays * 86400000);

  const apiFormatter = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Kyiv',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const apiParts = apiFormatter.formatToParts(shifted);
  const get = (type: string) => apiParts.find((p) => p.type === type)?.value ?? '';
  const api = `${get('year')}-${get('month')}-${get('day')}`;

  return { api };
}

function fmtCsvNum(v: number | undefined, digits: number): string {
  if (v == null || Number.isNaN(v)) return '';
  return v.toFixed(digits);
}

function escapeCsvField(field: string): string {
  if (field.includes(';') || field.includes('\n') || field.includes('"')) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}

function buildSemicolonCsv(rows: string[][]): string {
  return rows.map((row) => row.map(escapeCsvField).join(';')).join('\r\n');
}

export default function Home() {
  const navigate = useNavigate();
  const currentUser = getStoredUser();
  const { selectedAsset } = useSelectedAsset();

  useEffect(() => {
    if (!currentUser) {
      navigate('/login', { replace: true });
    }
  }, [currentUser, navigate]);

  const { t, i18n } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const { isDark } = useTheme();

  if (!currentUser) return null;

  const now = new Date();
  const hour = now.getHours();
  const greeting =
    hour < 12 ? t('greeting_morning') : hour < 18 ? t('greeting_day') : t('greeting_evening');

  const firstName = currentUser.displayName.split(' ')[0];

  const localeLang = i18n.language === 'pl' ? 'pl-PL' : i18n.language === 'en' ? 'en-US' : 'uk-UA';
  const dateStr = now.toLocaleDateString(localeLang, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const assetName = selectedAsset?.assetName ?? 'СЕС Табаки';
  const assetRegion = selectedAsset?.region ?? 'Одеська область';
  const assetCapacity = selectedAsset?.installedCapacityMw ?? 5;
  const assetId = selectedAsset?.id ?? '12a78662-a28d-40d5-9eb7-e83ebb158b41';

  const counterpartyLabel =
    selectedAsset?.counterpartyShortName
    ?? selectedAsset?.counterpartyName
    ?? null;

  const handleExport = async () => {
    if (!assetId) return;
    setIsExporting(true);
    setExportError(null);

    const todayParts = getKyivTodayParts();
    const offsets = [-2, -1, 0, 1, 2];
    const dates = offsets.map((o) => shiftKyivDate(todayParts, o).api);

    try {
      const responses = await Promise.all(
        dates.map((date) =>
          api.get<GenerationForecastDayResponse>(
            `/forecasting/assets/${assetId}/generation-forecasts/day?date=${date}`
          )
        )
      );

      const rows: string[][] = [];
      const header = [
        'Asset Name',
        'EIC Code',
        'Counterparty',
        'Date',
        'Time',
        'Status',
        'Forecast kWh',
        'Forecast Power kW',
        'Shortwave W/m²',
        'Direct W/m²',
        'Diffuse W/m²',
        'DNI W/m²',
        'Cloud %',
        'Temp °C',
        'Wind km/h',
        'Precip mm',
        'Model Version',
        'Target Type',
        'Weather Provider',
        'Forecast Run At',
      ];
      rows.push(header);

      for (const resp of responses) {
        const assetName = resp.asset?.name ?? '';
        const eicCode = resp.asset?.eicCode ?? '';
        const counterparty =
          resp.asset?.counterparty?.shortName ?? resp.asset?.counterparty?.name ?? '';
        const modelVersion = resp.modelVersion ?? '';
        const targetType = resp.targetType ?? '';
        const provider = resp.provider ?? resp.inputWeatherProvider ?? '';
        const date = resp.date ?? '';

        for (const h of resp.hourly ?? []) {
          const w = h.weather;
          rows.push([
            assetName,
            eicCode,
            counterparty,
            date,
            h.hour ?? '',
            h.status ?? '',
            fmtCsvNum(h.forecastEnergyKwh, 1),
            fmtCsvNum(h.forecastPowerKw, 1),
            fmtCsvNum(w?.shortwaveRadiation, 0),
            fmtCsvNum(w?.directRadiation, 0),
            fmtCsvNum(w?.diffuseRadiation, 0),
            fmtCsvNum(w?.directNormalIrradiance, 0),
            fmtCsvNum(w?.cloudCover, 1),
            fmtCsvNum(w?.temperature2m, 1),
            fmtCsvNum(w?.windSpeed10m, 1),
            fmtCsvNum(w?.precipitation, 1),
            modelVersion,
            targetType,
            provider,
            h.forecastRunAt ?? '',
          ]);
        }
      }

      const csvContent = buildSemicolonCsv(rows);
      const startDate = dates[0];
      const endDate = dates[dates.length - 1];
      const assetNameSlug = (selectedAsset?.assetName ?? 'SES_TABAKY')
        .toUpperCase()
        .replace(/\s+/g, '_');
      const filename = `generation_forecast_${assetNameSlug}_${startDate}_to_${endDate}.csv`;

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error('Export failed:', err);
      setExportError('Не вдалося сформувати звіт прогнозу');
    } finally {
      setIsExporting(false);
    }
  };

  // Кому показуємо портфельну головну (плитки СЕС) — перелік у @/utils/portfolioHome.
  if (hasPortfolioHome(currentUser)) return <PortfolioDashboard />;

  return (
    <DashboardLayout breadcrumb={[t('home_breadcrumb_home'), t('home_breadcrumb_dashboard')]}
>
      {/* Welcome header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4 md:mb-6 gap-3 md:gap-0">
        <div className="min-w-0">
          <h1
            className="text-lg md:text-2xl font-bold leading-tight"
            style={{ color: isDark ? '#E0E0E0' : '#1A1A1A', fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}
          >
            {greeting}, {firstName} 👋
          </h1>
          <p className="text-xs md:text-sm mt-1 flex-wrap" style={{ color: isDark ? '#6B6B70' : '#6B6B70', fontFamily: 'Inter, sans-serif' }}>
            {dateStr} &nbsp;·&nbsp; {assetName} &nbsp;·&nbsp; {assetRegion} &nbsp;·&nbsp; {assetCapacity} МВт
            {counterpartyLabel && (
              <>
                &nbsp;·&nbsp;
                <span className="font-medium" style={{ color: isDark ? '#E0E0E0' : '#1A1A1A' }}>
                  {counterpartyLabel}
                </span>
              </>
            )}
          </p>
        </div>
        <div className="flex flex-col items-start md:items-end gap-2 shrink-0">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium cursor-pointer transition-all hover:opacity-90 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: isDark ? 'rgba(255,255,255,0.05)' : '#fff',
                border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E5E5E8',
                color: isDark ? '#A0A0A8' : '#1A1A1A',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              <i className={`${isExporting ? 'ri-loader-4-line animate-spin' : 'ri-download-line'} text-xs md:text-sm`}></i>
              {isExporting ? 'Експорт...' : t('home_export')}
            </button>
            <button
              className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-semibold cursor-pointer transition-all hover:opacity-90 whitespace-nowrap"
              style={{
                background: 'color-mix(in srgb, var(--text) 10%, transparent)', color: 'var(--text)',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              <i className="ri-send-plane-line text-xs md:text-sm"></i> {t('home_submit_mms')}
            </button>
          </div>
          {exportError && (
            <span className="text-xs" style={{ color: 'var(--accent)' }}>{exportError}</span>
          )}
        </div>
      </div>

      {/* Live Telemetry Widget */}
      <HomeTelemetryWidget assetId={assetId} assetName={assetName} />

      {/* Daily Hourly Forecast — full width */}
      <div className="mb-4">
        <DailyHourlyForecastWidget
          assetId={assetId}
          hideTable={true}
          showActualLine
        />
      </div>

      {/* Forecast Generation Summary — full width */}
      <div className="mb-4">
        <ForecastGenerationSummaryWidget
          assetId={assetId}
          assetName={assetName}
          targetTypeLabel="Sale / Export Forecast"
        />
      </div>

      {/* Market widgets — full width, stacked */}
      <div className="flex flex-col gap-4 mb-4">
        <UeexMarketWidget />
        <OreeHourlyWidget />
      </div>

      {/* Operations row — 3 equal columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SkoInboxWidget />
        <AlertsPanel />
        <InboxPanel />
      </div>
    </DashboardLayout>
  );
}
